import { ArrowLeft, ExternalLink } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ASSETS = {
  logo: "/logo.png",
};

export default function Complaints() {
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <img src={ASSETS.logo} alt="Gaffer Athletics" className="h-8" />
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <h1 className="heading-xl text-3xl md:text-4xl text-white mb-8">Complaints & Dispute Resolution</h1>
        <p className="text-white/40 text-sm mb-12">Last updated: February 2026</p>

        <div className="space-y-12 body-text text-white/70">
          {/* Introduction */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Our Commitment to Customer Satisfaction</h2>
            <p>
              At Gaffer Athletics, we are committed to providing high-quality products and excellent customer service. If you are not satisfied with your purchase or have a complaint, we want to hear from you and resolve the issue as quickly as possible.
            </p>
          </section>

          {/* How to Complain */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">How to File a Complaint</h2>
            <p className="mb-4">
              If you have a complaint about a product or our service, please contact us directly first:
            </p>
            <ul className="list-none space-y-2 text-white/60">
              <li><strong className="text-white">Email:</strong> contact@gafferathletics.com</li>
              <li><strong className="text-white">Address:</strong> Gaffer Athletics ApS, 8870 Langå, Denmark</li>
            </ul>
            <p className="mt-4">
              Please include your order number, a description of the issue, and any supporting documentation (photos, receipts, etc.). We aim to respond to all complaints within 5 business days.
            </p>
          </section>

          {/* Our Process */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Our Complaint Handling Process</h2>
            <ol className="list-decimal list-inside space-y-3 text-white/60 ml-4">
              <li><strong className="text-white">Acknowledgment:</strong> We will acknowledge your complaint within 2 business days.</li>
              <li><strong className="text-white">Investigation:</strong> We will investigate the issue thoroughly.</li>
              <li><strong className="text-white">Resolution:</strong> We will propose a fair resolution within 14 days.</li>
              <li><strong className="text-white">Follow-up:</strong> We will follow up to ensure you are satisfied with the outcome.</li>
            </ol>
          </section>

          {/* External Dispute Resolution */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">External Dispute Resolution</h2>
            <p className="mb-4">
              If we cannot resolve your complaint to your satisfaction, you have the right to seek external assistance. As a consumer in the EU, you have access to the following dispute resolution options:
            </p>

            {/* Nævnenes Hus */}
            <div className="p-6 border border-white/10 bg-white/5 mb-6">
              <h3 className="heading-md text-lg text-white mb-3">Center for Complaint Resolution (Nævnenes Hus)</h3>
              <p className="mb-4 text-sm">
                The Center for Complaint Resolution (Nævnenes Hus) in Denmark offers mediation services for consumer disputes. You can file a complaint if we have not been able to resolve your issue directly.
              </p>
              <ul className="list-none space-y-2 text-white/60 text-sm">
                <li><strong className="text-white">Website:</strong> 
                  <a href="https://naevneneshus.dk" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white ml-2 inline-flex items-center gap-1">
                    naevneneshus.dk <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li><strong className="text-white">Address:</strong> Nævnenes Hus, Toldboden 2, 8800 Viborg, Denmark</li>
              </ul>
            </div>

            {/* EU ODR Platform */}
            <div className="p-6 border border-white/10 bg-white/5">
              <h3 className="heading-md text-lg text-white mb-3">EU Online Dispute Resolution (ODR) Platform</h3>
              <p className="mb-4 text-sm">
                The European Commission provides an Online Dispute Resolution platform for consumers who have purchased goods or services online. This platform helps consumers and traders resolve disputes without going to court.
              </p>
              <ul className="list-none space-y-2 text-white/60 text-sm">
                <li><strong className="text-white">ODR Platform:</strong> 
                  <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white ml-2 inline-flex items-center gap-1">
                    ec.europa.eu/consumers/odr <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              </ul>
              <p className="mt-4 text-sm">
                To use the ODR platform, you will need our email address: contact@gafferathletics.com
              </p>
            </div>
          </section>

          {/* Legal Action */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Legal Proceedings</h2>
            <p>
              Nothing in this complaints procedure affects your statutory rights. You retain the right to take legal action through the courts at any time. Danish law governs any disputes, and the Danish courts have jurisdiction, without prejudice to your rights under consumer protection laws in your country of residence.
            </p>
          </section>

          {/* Product Defects */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Product Defects and Warranty Claims</h2>
            <p className="mb-4">
              If your complaint relates to a defective product, please note:
            </p>
            <ul className="list-disc list-inside space-y-2 text-white/60 ml-4">
              <li>You have a 2-year right to complain about defects under Danish consumer law</li>
              <li>Defects appearing within 6 months are presumed to have existed at delivery</li>
              <li>We may offer repair, replacement, or refund depending on the circumstances</li>
              <li>Please retain proof of purchase and document the defect with photos</li>
            </ul>
          </section>

          {/* Contact */}
          <section>
            <h2 className="heading-md text-xl text-white mb-4">Contact Us</h2>
            <p>
              We are here to help. If you have any questions about our complaints procedure or need assistance, please don't hesitate to contact us at:
            </p>
            <p className="mt-4 text-white">
              Email: contact@gafferathletics.com
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 p-6 md:p-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/30 text-sm">
            © 2026 Gaffer Athletics. Engineering for the Sideline.
          </p>
        </div>
      </footer>
    </div>
  );
}
